"use client";

import "./globals.css";
import React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Inter } from "next/font/google";
import Script from "next/script";
import { AuthProvider, useAuth } from "./components/AuthProvider";
import AuthStatus from "./components/AuthStatus";
import LemonSqueezyScript from "./components/LemonSqueezyScript";

const inter = Inter({ subsets: ['latin'], weight: ['300', '400', '500', '600', '700'] });

function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { user, isLoading } = useAuth();

  // The home page has its own bar. Signed-in visitors should not get a second one.
  if (isLoading || !user || pathname === "/") {
    return <>{children}</>;
  }
  
  // Authenticated user: show app navigation and footer
  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0C1C3C]/90 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link href="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#14FF76] rounded-lg flex items-center justify-center">
              <span className="text-[#0C1C3C] font-bold text-sm tracking-tighter">CP</span>
            </div>
            <span className="font-semibold text-lg tracking-tight">CourtPicks AI</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-white/70">
            <Link href="/dashboard" className={`transition-colors ${pathname === '/dashboard' ? 'text-white' : 'hover:text-white'}`}>Dashboard</Link>
            <Link href="/completed" className={`transition-colors ${pathname === '/completed' ? 'text-white' : 'hover:text-white'}`}>History</Link>
            <Link href="/analytics" className={`transition-colors ${pathname === '/analytics' ? 'text-white' : 'hover:text-white'}`}>Results</Link>
            <Link href="/account" className={`transition-colors ${pathname === '/account' ? 'text-white' : 'hover:text-white'}`}>Account</Link>
            {user?.is_admin && (
              <Link href="/admin" className={`transition-colors ${pathname === '/admin' ? 'text-white' : 'hover:text-white'}`}>Admin</Link>
            )}
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
              <svg className="w-4 h-4 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
              </svg>
              <AuthStatus />
            </div>
          </div>
        </div>
      </nav>
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">{children}</div>
      </main>
      <footer className="relative z-10 py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-white/40">© 2026 CourtPicks AI. All rights reserved.</p>
            <p className="text-sm text-white/40 text-center">A decision tool, not betting advice.</p>
            <div className="flex items-center gap-6 text-sm text-white/40">
              <Link href="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link href="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link href="/contact" className="hover:text-white transition-colors">Support</Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

function BodyWithBackground({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const isLanding = pathname === "/";
  return (
    <body
      className={`text-white antialiased min-h-screen ${!isLanding ? "!bg-[#0C1C3C]" : ""}`}
      style={isLanding ? { background: "#000" } : undefined}
    >
      {children}
      {process.env.BILLING_PROVIDER === "lemonsqueezy" || process.env.NEXT_PUBLIC_BILLING_PROVIDER === "lemonsqueezy" ? (
        <LemonSqueezyScript />
      ) : null}
      <Script src="https://code.iconify.design/3/3.1.0/iconify.min.js" strategy="afterInteractive" />
    </body>
  );
}

export default function RootLayout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" className={inter.className}>
      <AuthProvider>
        <BodyWithBackground>
          <AppShell>{children}</AppShell>
        </BodyWithBackground>
      </AuthProvider>
    </html>
  );
}

